import { useState, useEffect, useRef } from 'react';
import { Logo } from '../common/Logo';
import { UserProfileMenu } from './UserProfileMenu';
import { NavLinks } from './NavLinks';
import { Clock, Sparkles, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModalContext } from '../../contexts/ModalContext';

import { useWeekendDetection } from '../../hooks/useWeekendDetection';
import { FireyEffect } from '../common/FireyEffect';
import { WeekendCampaignBanner } from './WeekendCampaignBanner';

interface NavbarProps {
  onLogoClick?: () => void;
  onClose?: () => void;
  showUserActions?: boolean;
  user?: {
    name: string;
    email: string;
    profilePicture?: string;
    subscriptionPlan?: string;
    subscriptionExpiry?: string;
    remainingMinutes?: number;
    isRecurring?: boolean;
  };
  subscriptionInfo?: {
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
    planName: string;
    isActive: boolean;
    expiryDate: string;
    minutesLeft: number;
  };
  isLoadingSubscription?: boolean;
  showLogoText?: boolean;
  shouldShowBanner?: boolean;
}

export const Navbar = ({
  user: propsUser,
  onLogoClick,
  onClose,
  showUserActions = true,
  subscriptionInfo: propSubscriptionInfo,
  isLoadingSubscription: propIsLoadingSubscription = false,
  showLogoText = false,
  shouldShowBanner = false
}: NavbarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
    planName: string;
    isActive: boolean;
    expiryDate: string;
    minutesLeft: number;
    isRecurring?: boolean;
    isUnlimited?: boolean;
  }>({
    planType: 'free',
    planName: 'Free Plan',
    isActive: false,
    expiryDate: '',
    minutesLeft: 0,
    isRecurring: false,
    isUnlimited: false
  });
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  const navbarRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Use the auth context to get real user data
  const { user: contextUser } = useAuth();

  // Use the modal context for logout
  const { openLogoutModal } = useModalContext();

  // Weekend detection hook
  const { isWeekend, timeLeft } = useWeekendDetection();


  // Update local subscription state when props change
  useEffect(() => {
    if (propSubscriptionInfo) {
      setSubscriptionInfo(propSubscriptionInfo);
      setIsLoadingSubscription(propIsLoadingSubscription);
    }
  }, [propSubscriptionInfo, propIsLoadingSubscription]);

  // Use props user if provided, otherwise use context user
  // This allows for both external user prop and context user
  const activeUser = {
    name: propsUser?.name || propsUser?.email?.split('@')[0] || 'User',
    email: propsUser?.email || contextUser?.email || '',
    profilePicture: propsUser?.profilePicture || contextUser?.profilePicture || '',
    profileImage: propsUser?.profilePicture || contextUser?.profilePicture || '',
    isRecurring: propsUser?.isRecurring || subscriptionInfo.isRecurring || false,
    subscriptionPlan: propsUser?.subscriptionPlan || subscriptionInfo.planName || 'Free Plan',
    subscriptionExpiry: propsUser?.subscriptionExpiry || subscriptionInfo.expiryDate || '',
    remainingMinutes: propsUser?.remainingMinutes !== undefined
      ? propsUser.remainingMinutes
      : (isLoadingSubscription ? 0 : subscriptionInfo.minutesLeft),
    isActive: subscriptionInfo.isActive || false,
    isUnlimited: subscriptionInfo.isUnlimited || false
  };


  // Update navbar height CSS variable
  const updateNavbarHeight = () => {
    if (navbarRef.current) {
      const height = navbarRef.current.offsetHeight;
      document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    }
  };

  // Format minutes into a more readable format
  const formatMinutes = (minutes: number = 0, isUnlimited: boolean = false): string => {
    // If it's weekend, show unlimited
    if (isWeekend) {
      return '∞ Mins.';
    }

    // If no minutes are available, check if subscription info is loaded
    if (minutes === undefined || minutes === null) {
      if (isLoadingSubscription) {
        return '0 Min.'; // Show 0 while loading
      }
      return isUnlimited ? '∞ Mins.' : `${subscriptionInfo.minutesLeft || 0} Min.`;
    }
    // Always display as minutes, not hours
    return isUnlimited ? '∞ Mins.' : `${minutes > 1 ? minutes + ' Mins.' : minutes + ' Min.'}`;
  };



  // Handle click on minutes badge
  const handleMinutesBadgeClick = () => {
    navigate('/subscription?tab=usage');
  };

  useEffect(() => {
    // Scroll handler
    const handleScroll = () => {
      const scrollPosition = window.scrollY > 10;
      setIsScrolled(scrollPosition);
      // Update the height on scroll to ensure dropdown positioning is correct
      updateNavbarHeight();
    };

    // Check mobile view with improved detection
    const checkMobileView = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileView(isMobile);
      updateNavbarHeight();
    };

    // Initialize on mount
    checkMobileView();
    updateNavbarHeight();

    // Set up ResizeObserver for more reliable detection
    const bodyResizeObserver = new ResizeObserver(() => {
      // We observe the body to detect viewport size changes
      checkMobileView();
    });

    bodyResizeObserver.observe(document.body);

    // Create a ResizeObserver to monitor size changes of the navbar
    const navbarResizeObserver = new ResizeObserver(() => {
      updateNavbarHeight();
    });

    if (navbarRef.current) {
      navbarResizeObserver.observe(navbarRef.current);
    }

    // Also listen for traditional events as fallback
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobileView);

    // Force a re-check after a short delay to handle some edge cases
    setTimeout(checkMobileView, 300);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobileView);
      bodyResizeObserver.disconnect();

      if (navbarRef.current) {
        navbarResizeObserver.unobserve(navbarRef.current);
      }
    };
  }, []);

  return (
    <nav
      ref={navbarRef}
      className={`
        fixed top-0 left-0 right-0 z-50
        backdrop-blur-xl bg-white/20 dark:bg-slate-900/20
        transition-all duration-300 py-3
        ${isScrolled ? 'border-b border-border-light/10 dark:border-border-dark/10 shadow-sm' : ''}
      `}
    >
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between h-14">
        <div className="flex items-center gap-6">
          <Logo onClick={onLogoClick} showText={!isMobileView || showLogoText} />
        </div>

        {/* Desktop Navigation - Center */}
        {!isMobileView && propsUser !== undefined && <NavLinks subscriptionInfo={subscriptionInfo} />}

        {propsUser !== undefined && <div className="flex items-center space-x-3">
          {showUserActions ? (
            <>
              {/* Minutes Badge */}
              <div className="relative">
                <button
                  className={`
                    flex items-center px-3 py-3 rounded-full border transition-all duration-200 focus:outline-none relative
                    ${isWeekend || activeUser.isUnlimited
                      ? 'bg-secondary-light/10 dark:bg-secondary-dark/10 text-secondary-light dark:text-secondary-dark border-secondary-light/20 dark:border-secondary-dark/30 hover:bg-secondary-light/15 dark:hover:bg-secondary-dark/25'
                      : 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark border-primary-light/20 dark:border-primary-dark/30 hover:bg-primary-light/15 dark:hover:bg-primary-dark/25'
                    }
                  `}
                  onMouseEnter={() => setIsTooltipVisible(true)}
                  onMouseLeave={() => setIsTooltipVisible(false)}
                  onClick={handleMinutesBadgeClick}
                  aria-label="View interview minutes usage"
                >
                  {/* Fiery effect for weekends - positioned outside the button */}
                  <FireyEffect isActive={isWeekend} />

                  <Clock size={16} className="mr-1.5 flex-shrink-0" />
                  <span className="text-sm font-medium inline-flex items-center">
                    {isWeekend || activeUser.isUnlimited ? (
                      <div className="flex flex-row items-center gap-1 h-[20px]">
                        <span className="text-xl">∞ </span>
                        <span className="">Mins.</span>
                      </div>
                    ) : (
                      formatMinutes(activeUser.remainingMinutes, activeUser.isUnlimited)
                    )}
                  </span>
                  <Sparkles size={14} className={`ml-1 ${subscriptionInfo.planType === 'free'
                    ? 'text-amber-500'
                    : 'text-green-500 dark:text-green-400'
                    }`} />
                </button>

                {/* Tooltip - Responsive version */}
                <div
                  className={`absolute z-10 ${isMobileView ? 'left-0 -bottom-28 w-64 max-w-[calc(100vw-32px)]' : '-bottom-24 left-1/2 transform -translate-x-1/2 w-64'}
                    bg-surface-light dark:bg-surface-dark 
                    text-text-primary-light dark:text-text-primary-dark px-3 py-2 rounded-lg text-xs
                    border border-border-light dark:border-border-dark shadow-md transition-opacity duration-200
                    ${isTooltipVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                  `}
                >
                  <div className={`absolute ${isMobileView ? 'top-0 left-4' : '-top-2 left-1/2 -translate-x-1/2'} w-2 h-2 rotate-45 
                    bg-surface-light dark:bg-surface-dark border-t border-l border-border-light dark:border-border-dark`}></div>
                  <div className="text-center md:text-left break-normal">
                    {isWeekend ? (
                      <div>
                        <div className="mb-1">#FreeMockWeekends - It's Weekend! Practice as many interviews you want, create your own, get feedback & improve - There's no Limit.</div>
                      </div>
                    ) : subscriptionInfo.isUnlimited
                      ? 'You have unlimited interview minutes with your current plan. No limits, no worries about any stoppage just focus on practicing & acing your interviews.'
                      : 'This badge shows the number of minutes left in your subscription plan. You can buy additional minutes of interviews if you have an active subscription.'}
                  </div>
                </div>
              </div>

              {/* User Profile Menu - Desktop & Mobile */}
              <UserProfileMenu
                user={activeUser}
                isMobile={isMobileView}
                onLogoutClick={openLogoutModal}
              />
            </>
          ) : onClose && (
            /* Close button for modal/overlay pages */
            <button
              onClick={onClose}
              className="p-2.5 rounded-full bg-surface-light/50 dark:bg-surface-dark/50 border border-border-light dark:border-border-dark hover:bg-surface-light/80 dark:hover:bg-surface-dark/80 transition-all duration-300"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-text-primary-light dark:text-text-primary-dark" />
            </button>
          )}
                </div>}
        </div>
        {shouldShowBanner && <WeekendCampaignBanner isBannerVisible={true} isMobileView={isMobileView} />}
      </nav>
  );
};

export default Navbar; 